import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Eye, Loader2, AlertTriangle, Briefcase } from "lucide-react";
import { API } from "../../App";

const auth = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

// Server-backed template preview. Renders the template against a REAL
// recent job (optionally scoped to a store location) through the exact
// same placeholder resolver used for live SMS/email sends, and surfaces
// unknown/empty placeholders before the user saves.
export const TemplateLivePreview = ({ content, disabled }) => {
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/store/locations`, auth())
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.locations || [];
        setLocations(list.filter((l) => l.active !== false));
      })
      .catch(() => setLocations([]));
  }, []);

  const runPreview = async () => {
    if (!content?.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        `${API}/settings/notifications/preview`,
        { content, location_id: locationId || null },
        auth()
      );
      setResult(res.data);
    } catch {
      setResult(null);
      setError("Preview failed — please try again");
    } finally {
      setLoading(false);
    }
  };

  const missing = result?.missing || [];

  return (
    <div className="space-y-2" data-testid="template-live-preview">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button" size="sm" variant="outline"
          className="h-7 text-xs"
          disabled={disabled || loading || !content?.trim()}
          onClick={runPreview}
          data-testid="template-preview-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Eye className="w-3 h-3 mr-1.5" />}
          Preview with real data
        </Button>
        {locations.length > 0 && (
          <Select value={locationId || "__all"} onValueChange={(v) => setLocationId(v === "__all" ? "" : v)}>
            <SelectTrigger className="h-7 w-auto min-w-[150px] text-xs" data-testid="template-preview-location">
              <SelectValue placeholder="Any location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Any location</SelectItem>
              {locations.map((l) => (
                <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {error && <p className="text-[11px] text-red-500">{error}</p>}

      {result && (
        <div className="rounded-lg bg-background/40 border border-theme p-2.5 space-y-2" data-testid="template-preview-result">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
              Real-data preview
            </p>
            {result.sample_job && (
              <p className="text-[10px] text-muted-foreground flex items-center gap-1 truncate">
                <Briefcase className="w-3 h-3 shrink-0" />
                {result.sample_source === "sample_data" ? "Sample data (no jobs yet)" : (
                  <>
                    {result.sample_job.job_id}
                    {result.sample_job.location_name ? ` · ${result.sample_job.location_name}` : ""}
                    {` · ${result.sample_job.time_format === "24h" ? "24-hour" : "12-hour"} · ${result.sample_job.timezone}`}
                  </>
                )}
              </p>
            )}
          </div>
          <p className="text-xs text-foreground whitespace-pre-wrap font-mono leading-relaxed" data-testid="template-preview-rendered">
            {result.rendered || "(empty message)"}
          </p>
          {missing.length > 0 && (
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-2" data-testid="template-preview-warnings">
              <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1 mb-1">
                <AlertTriangle className="w-3 h-3" /> Placeholders with missing data
              </p>
              <ul className="space-y-0.5">
                {missing.map((m) => (
                  <li key={m.placeholder} className="text-[10px] text-amber-700 dark:text-amber-300 font-mono">
                    {`{{${m.placeholder}}}`} — {m.status === "unknown" ? "not a supported placeholder (check spelling)" : m.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateLivePreview;
